import style from "./style.module.scss";
import React, { PureComponent, createContext } from "react";
import classnames from "classnames";
import { keyHandler } from "@jmc/utils/utils/keyHandler";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";

export type HEADING_BACKGROUNDS = "primary" | "transparent"; // TODO: Rebranding: This should be removed in after rebranding

interface Props {
    height?: string;
    title: string | JSX.Element;
    backgroundColor?: string;
    marginXl?: boolean;
    renderBreadcrumb?: () => JSX.Element;
    info?: string | JSX.Element;
    fullWidth?: boolean;
}

export const HeadingContext = createContext(null);

const HeadingConsumer = ({ children }: { children: any }) => (
    <HeadingContext.Consumer>
        {(context: any) => {
            if (!context) {
                throw new Error("Heading compound components cannot be rendered outside the Heading component");
            }
            return children(context);
        }}
    </HeadingContext.Consumer>
);

const Tab = ({
    children,
    active,
    onClick,
}: {
    children: React.ReactNode;
    active: boolean;
    onClick?: () => void;
}): JSX.Element => (
    <HeadingConsumer>
        {() => (
            <div
                onClick={onClick ? () => onClick() : null}
                onKeyDown={(e: KeyboardEvent): void => keyHandler(e.key, () => onClick())}
                role="button"
                tabIndex={0}
                className={classnames(style.tab, active ? style.active : null)}
            >
                <div>
                    <Typography>{children}</Typography>
                </div>
            </div>
        )}
    </HeadingConsumer>
);

/**
 * TODO: Rebranding: This component will be deprecated after rebranding.
 * Heading will always be a HeaderHero.
 */
export class Heading extends PureComponent<Props> {
    static Tab = Tab;

    render(): JSX.Element {
        const {
            title,
            backgroundColor = "primary",
            renderBreadcrumb,
            marginXl = false,
            height,
            info,
            fullWidth = false,
        } = this.props;

        const breadcrumb = renderBreadcrumb && renderBreadcrumb();

        return (
            <HeadingContext.Provider value={{ component: "heading" }}>
                <div
                    className={classnames(
                        style.element,
                        marginXl ? style.element__marginXl : "",
                        backgroundColor ? style[`background-${backgroundColor}`] : "",
                        info ? style.withinfo : "",
                        fullWidth ? style.fullWidth : "",
                    )}
                    style={{ minHeight: height !== null ? height : "auto" }}
                    data-test-id="Heading"
                >
                    {info && (
                        <Typography
                            allowedClassName={style.info}
                            color="inherit"
                            weight="400"
                            data-test-id="Heading.Info"
                        >
                            {info}
                        </Typography>
                    )}
                    {title && title !== "" && (
                        <Typography variant="h1" color="inherit" weight="500" data-test-id="Heading.Title">
                            {title}
                        </Typography>
                    )}
                </div>
                {breadcrumb && (
                    <div
                        className={classnames(style.breadcrumb, marginXl ? style.breadcrumb__marginXl : "")}
                        data-test-id="Heading.Breadcrumb"
                    >
                        {breadcrumb}
                    </div>
                )}
            </HeadingContext.Provider>
        );
    }
}
